import { baseApi } from '../../../../store/api/baseApi';

// ========== Query Arg Types ==========

export interface KvsResult6to8QueryArgs {
  year: number;
  term: number;
}

export interface TopPerformers6to8QueryArgs extends KvsResult6to8QueryArgs {
  order?: 'asc' | 'desc';
}

export interface Student6to8DetailQueryArgs {
  year: number;
  term: number;
  studentid: number;
}

export interface Ro6to8QueryArgs {
  year: number;
  term: number;
  roid: number;
}

export interface School6to8DetailQueryArgs {
  year: number;
  term: number;
  kvid: number;
}

// ========== Response Types ==========

export interface KvsResult6to8SummaryClassData {
  totalStudents: number;
  avgMarks: number;
  needSupport: number;
  excellentStudents: number;
}

export interface KvsResult6to8SummaryResponse {
  success: boolean;
  data: {
    year: number;
    term: string;
    totalStudents: number;
    avgMarks: number;
    needSupport: number;
    excellentStudents: number;
    byClass: Record<string, KvsResult6to8SummaryClassData>;
  };
  message: string;
}

export interface Subject6to8DistributionItem {
  subjectTitle: string;
  avgGrandTotal: number;
  avgWeightage: number;
  totalStudents: number;
}

export interface KvsResult6to8SubjectwiseResponse {
  success: boolean;
  data: {
    year: number;
    term: string;
    overall: Subject6to8DistributionItem[];
    byClass: Record<string, Subject6to8DistributionItem[]>;
  };
  message: string;
}

export interface Performance6to8Distribution {
  excellent: number;
  good: number;
  average: number;
  belowAverage: number;
  total: number;
}

export interface KvsResult6to8DistributionResponse {
  success: boolean;
  data: {
    year: number;
    term: string;
    overall: Performance6to8Distribution;
    byClass: Record<string, Performance6to8Distribution>;
  };
  message: string;
}

export interface Region6to8TopBottomSchool {
  schoolID: number;
  schoolName: string;
  avgMarksPercentage: number;
  totalStudents: number;
}

export interface Region6to8CompetencyItem {
  roID: number;
  roName: string;
  avgMarksPercentage: number;
  totalStudents: number;
  topSchool: Region6to8TopBottomSchool;
  bottomSchool: Region6to8TopBottomSchool;
}

export interface KvsResult6to8RegionwiseResponse {
  success: boolean;
  data: {
    year: number;
    term: string;
    regions: Region6to8CompetencyItem[];
    byClass?: Record<string, Region6to8CompetencyItem[]>;
  };
  message: string;
}

export interface TopPerformer6to8Region {
  rank: number;
  roID: number;
  roName: string;
  avgMarksPercentage: number;
  totalStudents: number;
}

export interface KvsResult6to8TopPerformersResponse {
  success: boolean;
  data: {
    year: number;
    term: string;
    order: string;
    regions: TopPerformer6to8Region[];
  };
  message: string;
}

/**
 * Pass/fail counts derived from the `result` column (1 = pass, 2 = fail,
 * 0 = not declared). `passPercentage` is pass / (pass + fail) × 100 and is
 * null — not 0 — when nothing was declared.
 */
export interface PassFailCounts {
  passed: number;
  failed: number;
  notDeclared: number;
  passPercentage: number | null;
}

export interface RoSchool6to8ClassData extends PassFailCounts {
  totalStudents: number;
  avgMarks: number;
  needSupport: number;
  excellentStudents: number;
}

export interface RoSchool6to8Item extends PassFailCounts {
  kvID: number;
  schoolID: number;
  schoolName: string;
  totalStudents: number;
  avgMarks: number;
  needSupport: number;
  excellentStudents: number;
  byClass: Record<string, RoSchool6to8ClassData | null>;
}

export interface KvsResult6to8RoDetailsResponse {
  success: boolean;
  data: {
    year: number;
    term: string;
    roID: number;
    roName: string;
    totalSchools: number;
    schools: RoSchool6to8Item[];
  };
  message: string;
}

export interface School6to8Student {
  studentID: number;
  studentName: string | null;
  section: string;
  marks: number;
  result: number;
  resultStatus: number;
  workingDay: number;
  presentDay: number;
  rollNo: number | null;
  cbseRollNo?: string | null;
  academicSession?: string | null;
  stream?: number | null;
}

export interface School6to8ClassData {
  totalStudents: number;
  avgMarks: number;
  needSupport: number;
  excellentStudents: number;
  students: School6to8Student[];
}

export interface School6to8DetailData {
  kvID: number;
  schoolID: number;
  schoolName: string;
  roID: number;
  roName: string;
  year: number;
  term: string;
  byClass: Record<string, School6to8ClassData>;
}

export interface KvsResult6to8SchoolDetailResponse {
  success: boolean;
  data: School6to8DetailData;
  message: string;
}

export interface Student6to8Detail {
  studentID: number;
  studentName: string | null;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  section: string;
  marks: number;
  result: number;
  resultStatus: number;
  workingDay: number;
  presentDay: number;
  rollNo: number | null;
  cbseRollNo?: string | null;
  academicSession?: string | null;
  stream?: number | null;
  schoolName?: string;
  class?: string | number;
  [key: string]: any;
}

export interface KvsResult6to8StudentDetailResponse {
  success: boolean;
  data: Student6to8Detail | Student6to8Detail[];
  message?: string;
}

// ========== RTK Query Endpoints ==========

export const kvsResult6to8Api = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getKvsResult6to8Summary: build.query<KvsResult6to8SummaryResponse, KvsResult6to8QueryArgs>({
      query: ({ year, term }) => `/kvsresult6to8/summary?term=${term}&year=${year}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResult6to8Subjectwise: build.query<KvsResult6to8SubjectwiseResponse, KvsResult6to8QueryArgs>({
      query: ({ year, term }) => `/kvsresult6to8/subjectwise-distribution?term=${term}&year=${year}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResult6to8Distribution: build.query<KvsResult6to8DistributionResponse, KvsResult6to8QueryArgs>({
      query: ({ year, term }) => `/kvsresult6to8/6to8-distribution?term=${term}&year=${year}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResult6to8Regionwise: build.query<KvsResult6to8RegionwiseResponse, KvsResult6to8QueryArgs>({
      query: ({ year, term }) => `/kvsresult6to8/regionwise-competency?term=${term}&year=${year}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResult6to8TopPerformers: build.query<KvsResult6to8TopPerformersResponse, TopPerformers6to8QueryArgs>({
      query: ({ year, term, order = 'desc' }) =>
        `/kvsresult6to8/top-performers-regions?term=${term}&year=${year}&order=${order}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResult6to8RoDetails: build.query<KvsResult6to8RoDetailsResponse, Ro6to8QueryArgs>({
      query: ({ year, term, roid }) =>
        `/kvsresult6to8/6to8-result-rodetails?term=${term}&year=${year}&roid=${roid}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResult6to8SchoolDetail: build.query<KvsResult6to8SchoolDetailResponse, School6to8DetailQueryArgs>({
      query: ({ year, term, kvid }) =>
        `/kvsresult6to8/6to8-school-detail?term=${term}&year=${year}&kvid=${kvid}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResult6to8StudentDetail: build.query<KvsResult6to8StudentDetailResponse, Student6to8DetailQueryArgs>({
      query: ({ year, term, studentid }) =>
        `/kvsresult6to8/6to8-student-detail?term=${term}&year=${year}&studentid=${studentid}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetKvsResult6to8SummaryQuery,
  useGetKvsResult6to8SubjectwiseQuery,
  useGetKvsResult6to8DistributionQuery,
  useGetKvsResult6to8RegionwiseQuery,
  useGetKvsResult6to8TopPerformersQuery,
  useGetKvsResult6to8RoDetailsQuery,
  useGetKvsResult6to8SchoolDetailQuery,
  useGetKvsResult6to8StudentDetailQuery,
} = kvsResult6to8Api;
